// src/app/app.component.ts
import { Component, OnInit, AfterViewInit, ViewChildren, QueryList, inject } from '@angular/core';
import { NavController, IonRouterOutlet } from '@ionic/angular';
import { AuthService } from '@core/services/auth.service';
import { AppRoutingService } from '@core/services/app-routing.service';
import { addIcons } from 'ionicons';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import {
  person,
  personOutline,
  document,
  documentOutline,
  barbell,
  barbellOutline,
  playCircleOutline
} from 'ionicons/icons';
import { take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { ThemeService } from '@core/services/theme.service';
import { EmailChangeService } from '@core/services/email-change.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
})
export class AppComponent implements OnInit, AfterViewInit {
  private authService = inject(AuthService);
  private routingService = inject(AppRoutingService);
  private navCtrl = inject(NavController);
  private router = inject(Router);
  private http = inject(HttpClient);
  private themeService = inject(ThemeService);
  private emailChangeService = inject(EmailChangeService);
  private alreadyHandled = false;

  @ViewChildren(IonRouterOutlet)
  private routerOutlets!: QueryList<IonRouterOutlet>;

  ngAfterViewInit() {
    setTimeout(() => {
      this.setupRouterOutlets();
    }, 150);
  }

  private setupRouterOutlets() {
    if (!this.routerOutlets) return;

    this.routerOutlets.forEach(outlet => {
      outlet.swipeGesture = false;
    });

    console.log('🚫 swipeGesture DESATIVADO globalmente em todos IonRouterOutlet');
  }

  /**
   * Registra listeners globais relacionados ao ciclo de vida
   * da aplicação e consistência da autenticação.
   *
   * Estes listeners são executados somente quando o usuário
   * retorna para a aplicação, não realizando polling nem
   * verificações contínuas.
   */
  private registerSessionValidationListeners(): void {
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
        return;
      }

      void this.validateSessionOnFocus();
    });

    window.addEventListener('focus', () => {
      void this.validateSessionOnFocus();
    });

    globalThis.document.addEventListener('visibilitychange', () => {
      if (globalThis.document.visibilityState === 'visible') {
        void this.validateSessionOnFocus();
      }
    });
  }

  /**
   * =====================================================
   * Validação central de consistência da sessão.
   * =====================================================
   *
   * Executada quando a aplicação retorna ao foco.
   *
   * Responsabilidades atuais e futuras:
   *
   * 1. detectar logout remoto;
   * 2. detectar alteração de e-mail;
   * 3. garantir sincronização entre múltiplas abas,
   *    navegadores e dispositivos.
   *
   * O fluxo privilegia eventos locais quando disponíveis,
   * mas a fonte definitiva permanece sendo Firebase
   * Authentication + Firestore.
   */
  private async validateSessionOnFocus(): Promise<void> {
    const currentUrl = this.router.url || '';

    const isEmailChangeProtectedRoute =
      currentUrl === '/email-change' ||
      currentUrl.startsWith('/email-change?') ||
      currentUrl.startsWith('/email-change/');

    const isProtectedRoute =
      currentUrl.startsWith('/tabs') ||
      currentUrl.startsWith('/settings') ||
      isEmailChangeProtectedRoute ||
      currentUrl.startsWith('/edit-profile') ||
      currentUrl.startsWith('/subscription') ||
      currentUrl.startsWith('/evaluation');

    if (!isProtectedRoute) {
      return;
    }

    // =====================================================
    // EVENTO LOCAL DE ALTERAÇÃO DE E-MAIL
    // -----------------------------------------------------
    // Mesmo navegador/dispositivo.
    // Evita esperar nova sincronização do Firebase quando
    // já existe evidência local da alteração.
    // =====================================================
    const emailChangeEvent =
      this.emailChangeService.readDetectedEmailChange();

    if (emailChangeEvent) {
      this.emailChangeService.clearDetectedEmailChange();
      this.emailChangeService.saveSigninEmailChangeAlert({
        ...emailChangeEvent,
        reason: 'local-event',
      });

      await this.authService.signOut();

      await this.navCtrl.navigateRoot('/registration/signin', {
        replaceUrl: true,
        animated: false,
      });

      return;
    }

    // =====================================================
    // VALIDAÇÃO REMOTA DE CONSISTÊNCIA
    // -----------------------------------------------------
    // Cobre cenários onde o evento local não existe:
    //
    // • outro navegador;
    // • outro dispositivo;
    // • futura versão Capacitor;
    // • sessão antiga restaurada.
    //
    // A fonte da verdade permanece sendo:
    // Firebase Authentication + Firestore.
    // =====================================================
    const validation =
      await this.authService.validateCurrentSessionConsistency();

    if (validation.isValid) {
      return;
    }

    if (
      validation.reason === 'email-changed' &&
      validation.newEmail
    ) {
      this.emailChangeService.saveSigninEmailChangeAlert({
        uid: validation.uid || '',
        oldEmail: validation.oldEmail,
        newEmail: validation.newEmail,
        changedAt: Date.now(),
        reason: 'auth-firestore-mismatch',
      });
    }

    await this.authService.signOut();

    await this.navCtrl.navigateRoot('/registration/signin', {
      replaceUrl: true,
      animated: false,
    });
  }

  // =====================================================
  // VERSION CHECK — invalidação agressiva Safari/iOS
  // -----------------------------------------------------
  // Objetivo:
  // - detectar build novo deployado
  // - impedir SPA antiga persistida em memória
  // - forçar hard reload real no Safari
  // =====================================================
  private async checkAppVersion(): Promise<void> {
    try {

      // Cache-buster para impedir cache do Safari
      const timestamp = Date.now();

      const response = await firstValueFrom(
        this.http.get<{ version: string }>(
          `/assets/version.json?t=${timestamp}`
        )
      );

      const remoteVersion = response.version;
      const currentVersion = environment.appVersion;

      console.log(
        '[VersionCheck]',
        'local:',
        currentVersion,
        'remote:',
        remoteVersion
      );

      // =================================================
      // BUILD NOVO DETECTADO
      // =================================================
      if (remoteVersion !== currentVersion) {

        console.warn(
          '[VersionCheck] Nova versão detectada. Forçando hard reload.'
        );

        // Remove BFCache/state antigo do Safari
        sessionStorage.clear();

        // =================================================
        // HARD RELOAD PRESERVANDO O DEEP LINK
        // -------------------------------------------------
        // Mantém:
        // - pathname atual;
        // - query params funcionais (ex.: token);
        // - hash, quando existente.
        //
        // Atualiza somente o parâmetro técnico "v",
        // utilizado para invalidar a versão anterior.
        //
        // Exemplo:
        // /email-change-confirm?token=ABC
        //
        // torna-se:
        // /email-change-confirm?token=ABC&v=2026.09.03.150242
        // =================================================
        const reloadUrl = new URL(window.location.href);

        reloadUrl.searchParams.set('v', remoteVersion);

        window.location.replace(reloadUrl.toString());

        return;
      }

    } catch (error) {

      console.warn(
        '[VersionCheck] Falha ao verificar versão:',
        error
      );
    }
  }

  ngOnInit() {
    // =================================================
    // CHECK DE VERSÃO (Safari cache invalidation)
    // =================================================
    this.checkAppVersion();

    // Baixa todos os SVGs no boot da aplicação
    addIcons({
      person,
      personOutline,
      document,
      documentOutline,
      barbell,
      barbellOutline,
      playCircleOutline
    });

    /**
     * =====================================================
     * SESSION VALIDATION LISTENERS
     * -----------------------------------------------------
     * Centraliza todos os listeners responsáveis por manter
     * a sessão consistente em navegadores modernos,
     * principalmente Safari/iOS.
     *
     * Responsabilidades:
     *
     * • BFCache (pageshow.persisted)
     *   - força reload completo quando o Safari restaura
     *     uma página do Back-Forward Cache.
     *
     * • focus
     *   - revalida a sessão quando a aba volta ao foco.
     *
     * • visibilitychange
     *   - detecta retorno da aplicação após troca de aba,
     *     abertura de links externos (ex.: Gmail),
     *     Universal Links ou navegação do Safari.
     *
     * Objetivos:
     *
     * - impedir uso de sessão inválida;
     * - sincronizar múltiplas abas;
     * - refletir logout remoto;
     * - refletir alteração de e-mail;
     * - evitar inconsistências causadas pelo BFCache do Safari.
     * =====================================================
     */
    this.registerSessionValidationListeners();

    /* =====================================================
     * AJUSTE DEFINITIVO (SEM PATCH ESPALHADO)
     * -----------------------------------------------------
     * Com getAuthState() agora reativo (docData), o AppComponent
     * NÃO pode reagir a cada emissão (senão atropela navegações).
     *
     * Estratégia mínima:
     * - lê APENAS 1 emissão (bootstrap) com take(1)
     * - mantém o guard alreadyHandled + isBootstrap como proteção extra
     * ===================================================== */
    this.authService
      .getAuthState()
      .pipe(take(1))
      .subscribe(async user => {
        console.log(
          'AppComponent: authState emitido - user:',
          user?.uid ?? 'null',
          'status:',
          user?.userJourneyStatus ?? 'N/A',
          'lastStep:',
          user?.lastStep ?? 'N/A'
        );

        /* =====================================================
        * BLOQUEIO GLOBAL DURANTE VERIFICAÇÃO DE E-MAIL
        * ===================================================== */
        if (this.authService.isProcessingEmailVerification()) {
          console.log('AppComponent: bypass ativo (processingEmailVerification)');
          return;
        }

        /* =====================================================
         * EXECUÇÃO ÚNICA (BOOTSTRAP-SAFE)
         * -----------------------------------------------------
         * Evita que o AppComponent "mande" na navegação o tempo todo.
         * Ele só decide rota UMA vez no bootstrap, e depois deixa:
         * - guards de módulo
         * - componentes (Payment/Profile/etc.)
         * controlarem o fluxo.
         *
         * Motivação:
         * - getAuthState() agora é stream reativo do Firestore
         * - então, redirecionar em toda emissão vai atropelar
         *   navegações válidas (ex.: pós-pagamento / pós-update doc)
         * ===================================================== */
        if (this.alreadyHandled) {
          return;
        }

        // Só trata como bootstrap quando a URL está neutra (raiz)
        // (evita interferir em rotas profundas: /subscription, /tabs, /evaluation, etc.)
        const path = window.location.pathname || '';
        const isBootstrap = path === '/' || path === '' || path === '/index.html';
        if (!isBootstrap) {
          // Não marca alreadyHandled aqui para não travar o bootstrap real
          // caso o app seja carregado direto numa rota profunda (deep link).
          return;
        }

        this.alreadyHandled = true;

        /* =====================================================
        * USUÁRIO DESLOGADO → SIGNIN
        * ===================================================== */
       if (!user) {
         await this.navCtrl.navigateRoot('/registration/signin', {
            replaceUrl: true,
            animated: false
          });
          return;
        }

        /* =====================================================
        * HUB ÚNICO DE DECISÃO DE ROTA
        * A rota correta é SEMPRE derivada do estado do usuário
        * ===================================================== */
        const targetRoute = await this.routingService.getRouteFromStatus(
          user.userJourneyStatus
        );

        console.log('AppComponent: Redirecionando para rota correta:', targetRoute);

        await this.router.navigateByUrl(targetRoute, {
          replaceUrl: true
        });
      });
  }
}
